import {
  Checkbox,
  Modal,
  ModalButton,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Text,
} from '@vtex/admin-ui'
import { STORAGE, useCache, useDrawerSubForm } from 'hooks'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Event } from 'typings'
import { useAnalytics } from 'use-analytics'
import { messages } from './messages'
import { styles } from './styles'
import {
  DuplicateCheckbox,
  RegularCheckboxProps,
  type DuplicateForm,
  type DuplicateModalProps,
  type DuplicateSections,
} from './typing'

const DUPLICATE_SECTIONS_CACHE_KEY = 'duplicate-sections'

function CollapsibleCheckbox({
  label,
  state,
  children,
}: {
  label: string
  state: {
    value: boolean | 'indeterminate'
    setValue: (value: boolean | 'indeterminate') => void
  }
  children?: React.ReactNode
}) {
  const checkboxRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = state.value === 'indeterminate'
    }
  }, [state.value])

  const handleChange = () => {
    const currentValue = state.value
    let newValue: boolean | 'indeterminate'

    if (currentValue === true) {
      newValue = false
    } else if (currentValue === false) {
      newValue = true
    } else {
      newValue = true
    }

    state.setValue(newValue)
  }

  return (
    <div>
      <Checkbox
        ref={checkboxRef}
        label={label}
        checked={state.value === true || state.value === 'indeterminate'}
        onChange={handleChange}
      />
      <div className={styles.nestedContainer}>{children}</div>
    </div>
  )
}

const checkboxConfigMap = {
  [DuplicateCheckbox.COMMERCIAL_POLICIES]: {
    messageKey: 'commercialPolicies.label' as const,
  },
  [DuplicateCheckbox.SELLERS]: {
    messageKey: 'sellers.label' as const,
  },
  [DuplicateCheckbox.ITEMS_AND_BENEFITS]: {
    messageKey: 'itemsAndBenefits.label' as const,
  },
  [DuplicateCheckbox.CONDITIONS]: {
    messageKey: 'conditions.label' as const,
  },
  [DuplicateCheckbox.RESTRICTIONS]: {
    messageKey: 'restrictions.label' as const,
  },
  [DuplicateCheckbox.ACCUMULATION]: {
    messageKey: 'accumulation.label' as const,
  },
  [DuplicateCheckbox.STOREFRONT]: {
    messageKey: 'storefront.label' as const,
  },
}

const RegularCheckbox = memo(
  ({
    checkboxType,
    messageKey,
    drawerForm,
    onCheckboxChange,
    $t,
    isChecked,
  }: RegularCheckboxProps) => {
    const messagePath = useMemo(
      () =>
        messageKey.split('.').reduce((obj, key) => obj[key], messages as any),
      [messageKey]
    )

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked
        drawerForm.setValue(checkboxType, newValue)
        onCheckboxChange(checkboxType, newValue)
      },
      [checkboxType, drawerForm, onCheckboxChange]
    )

    const translatedLabel = useMemo(() => $t(messagePath), [messagePath, $t])

    return (
      <Checkbox
        label={translatedLabel}
        {...drawerForm.register(checkboxType)}
        checked={isChecked}
        onChange={handleChange}
      />
    )
  }
)

RegularCheckbox.displayName = 'RegularCheckbox'

export function DuplicateModal(props: DuplicateModalProps) {
  const { state, onConfirm, ...rest } = props
  const { formatMessage: $t } = useIntl()
  const cache = useCache()
  const { track } = useAnalytics()

  const config: DuplicateForm = useMemo(
    () => ({
      [DuplicateCheckbox.NAME_AND_DESCRIPTION]: true,
      [DuplicateCheckbox.DATES_AND_RECURRENCE]: true,
      [DuplicateCheckbox.COMMERCIAL_POLICIES]: true,
      [DuplicateCheckbox.SELLERS]: true,
      [DuplicateCheckbox.ITEMS_AND_BENEFITS]: true,
      [DuplicateCheckbox.CONDITIONS]: true,
      [DuplicateCheckbox.RESTRICTIONS]: true,
      [DuplicateCheckbox.ACCUMULATION]: true,
      [DuplicateCheckbox.STOREFRONT]: true,
    }),
    []
  )

  const { drawerForm } = useDrawerSubForm<DuplicateForm>({
    config,
    isOpen: state.open,
  })

  const [isGeneralIndeterminate, setIsGeneralIndeterminate] = useState(false)
  const [generalValue, setGeneralValue] = useState(true)

  const [formValues, setFormValues] = useState<DuplicateForm>(config)

  useEffect(() => {
    if (state.open) {
      track(Event.DUPLICATE_MODAL_VIEW)
    }
  }, [state.open, track])

  useEffect(() => {
    const subscription = drawerForm.watch((values) => {
      const safeValues: DuplicateForm = Object.values(DuplicateCheckbox).reduce(
        (acc, field) => {
          if (field !== DuplicateCheckbox.GENERAL) {
            acc[field] = Boolean(values[field])
          }
          return acc
        },
        {} as DuplicateForm
      )
      setFormValues(safeValues)

      const nameChecked = values[DuplicateCheckbox.NAME_AND_DESCRIPTION]
      const datesChecked = values[DuplicateCheckbox.DATES_AND_RECURRENCE]

      if (nameChecked && datesChecked) {
        setGeneralValue(true)
        setIsGeneralIndeterminate(false)
      } else if (!nameChecked && !datesChecked) {
        setGeneralValue(false)
        setIsGeneralIndeterminate(false)
      } else {
        setGeneralValue(false)
        setIsGeneralIndeterminate(true)
      }
    })

    return () => subscription.unsubscribe()
  }, [drawerForm])

  const handleGeneralChange = useCallback(
    (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        drawerForm.setValue(DuplicateCheckbox.NAME_AND_DESCRIPTION, true)
        drawerForm.setValue(DuplicateCheckbox.DATES_AND_RECURRENCE, true)
      } else if (checked === false) {
        drawerForm.setValue(DuplicateCheckbox.NAME_AND_DESCRIPTION, false)
        drawerForm.setValue(DuplicateCheckbox.DATES_AND_RECURRENCE, false)
      }
    },
    [drawerForm]
  )

  const handleCheckboxChange = useCallback(
    (checkboxName: DuplicateCheckbox, newValue: boolean | 'indeterminate') => {
      track(Event.DUPLICATE_MODAL_CHANGE_CLICK, {
        checkbox: checkboxName,
        newValue,
      })
    },
    [track]
  )

  const handleConfirm = useCallback(() => {
    const currentValues = drawerForm.getValues()

    const regularSections = Object.values(DuplicateCheckbox).reduce(
      (acc, field) => {
        if (
          field !== DuplicateCheckbox.GENERAL &&
          field !== DuplicateCheckbox.NAME_AND_DESCRIPTION &&
          field !== DuplicateCheckbox.DATES_AND_RECURRENCE
        ) {
          acc[field] = Boolean(currentValues[field])
        }
        return acc
      },
      {} as Omit<DuplicateSections, DuplicateCheckbox.GENERAL>
    )

    const selectedSections: DuplicateSections = {
      [DuplicateCheckbox.GENERAL]: {
        selected: Boolean(generalValue === true || isGeneralIndeterminate),
        [DuplicateCheckbox.NAME_AND_DESCRIPTION]: Boolean(
          currentValues[DuplicateCheckbox.NAME_AND_DESCRIPTION]
        ),
        [DuplicateCheckbox.DATES_AND_RECURRENCE]: Boolean(
          currentValues[DuplicateCheckbox.DATES_AND_RECURRENCE]
        ),
      },
      ...regularSections,
    }

    cache.set(DUPLICATE_SECTIONS_CACHE_KEY, selectedSections, STORAGE.SESSION)

    onConfirm(selectedSections)
    track(Event.DUPLICATE_MODAL_CONFIRM_CLICK, {
      sections: selectedSections,
    })
    state.hide()
  }, [
    drawerForm,
    generalValue,
    isGeneralIndeterminate,
    cache,
    onConfirm,
    track,
    state,
  ])

  const handleCancel = useCallback(() => {
    track(Event.DUPLICATE_MODAL_CANCEL_CLICK)
    state.hide()
  }, [track, state])

  const regularCheckboxes = Object.keys(
    checkboxConfigMap
  ) as DuplicateCheckbox[]

  const handleNameDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked
      drawerForm.setValue(DuplicateCheckbox.NAME_AND_DESCRIPTION, newValue)
      handleCheckboxChange(DuplicateCheckbox.NAME_AND_DESCRIPTION, newValue)
    },
    [drawerForm, handleCheckboxChange]
  )

  const handleDatesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked
      drawerForm.setValue(DuplicateCheckbox.DATES_AND_RECURRENCE, newValue)
      handleCheckboxChange(DuplicateCheckbox.DATES_AND_RECURRENCE, newValue)
    },
    [drawerForm, handleCheckboxChange]
  )

  const handleGeneralStateChange = useCallback(
    (newValue: boolean | 'indeterminate') => {
      if (newValue === true) {
        setGeneralValue(true)
        setIsGeneralIndeterminate(false)
      } else if (newValue === false) {
        setGeneralValue(false)
        setIsGeneralIndeterminate(false)
      }
      handleGeneralChange(newValue)
      handleCheckboxChange(DuplicateCheckbox.GENERAL, newValue)
    },
    [handleGeneralChange, handleCheckboxChange]
  )

  const translatedLabels = useMemo(
    () => ({
      title: $t(messages.title),
      subtitle: $t(messages.subtitle),
      generalLabel: $t(messages.general.label),
      generalName: $t(messages.general.name),
      generalDates: $t(messages.general.dates),
      cancelButton: $t(messages.buttons.cancel),
      duplicateButton: $t(messages.buttons.duplicate),
    }),
    [$t]
  )

  return (
    <Modal
      state={state}
      size="medium"
      portal
      {...rest}
      onClick={(e) => e.stopPropagation()}
    >
      <ModalHeader>
        <ModalTitle>{translatedLabels.title}</ModalTitle>
        <ModalDismiss onClick={handleCancel} />
      </ModalHeader>

      <ModalContent className={styles.modalContent}>
        <Text>{translatedLabels.subtitle}</Text>
        <CollapsibleCheckbox
          label={translatedLabels.generalLabel}
          state={{
            value: isGeneralIndeterminate ? 'indeterminate' : generalValue,
            setValue: handleGeneralStateChange,
          }}
        >
          <Checkbox
            label={translatedLabels.generalName}
            {...drawerForm.register(DuplicateCheckbox.NAME_AND_DESCRIPTION)}
            checked={formValues[DuplicateCheckbox.NAME_AND_DESCRIPTION]}
            onChange={handleNameDescriptionChange}
          />
          <Checkbox
            label={translatedLabels.generalDates}
            {...drawerForm.register(DuplicateCheckbox.DATES_AND_RECURRENCE)}
            checked={formValues[DuplicateCheckbox.DATES_AND_RECURRENCE]}
            onChange={handleDatesChange}
          />
        </CollapsibleCheckbox>

        {regularCheckboxes.map((checkboxType) => (
          <RegularCheckbox
            key={checkboxType}
            checkboxType={checkboxType}
            messageKey={checkboxConfigMap[checkboxType].messageKey}
            drawerForm={drawerForm}
            onCheckboxChange={handleCheckboxChange}
            $t={$t}
            isChecked={formValues[checkboxType]}
          />
        ))}
      </ModalContent>

      <ModalFooter className={styles.modalFooter}>
        <ModalButton
          variant="secondary"
          onClick={handleCancel}
          dismissModal
          size="large"
        >
          {translatedLabels.cancelButton}
        </ModalButton>
        <ModalButton size="large" onClick={handleConfirm}>
          {translatedLabels.duplicateButton}
        </ModalButton>
      </ModalFooter>
    </Modal>
  )
}
